// penagihan.view — menu "📣 Penagihan & Pengingat": DAFTAR KERJA kirim WA ke banyak pelanggan.
//
// Kenapa bukan "blast": WhatsApp tidak bisa dikirimi pesan massal dari aplikasi web — yang bisa
// dilakukan hanya membuka satu percakapan berisi teks siap kirim. Jadi yang benar-benar menolong
// CS bukan tombol "kirim semua" palsu, melainkan daftar yang MENGINGAT posisi: siapa sudah
// dihubungi, kapan, dan siapa yang belum. Pekerjaan 50 nota bisa dijeda lalu dilanjut besok.
//
// Sumber data sengaja MENUMPANG layar yang sudah benar, bukan kriteria sendiri:
//   • piutang        → `listPayable` (finScope: tiket tutup-buku tak ikut; klaim garansi ikut)
//   • belum diambil  → `fetchPerluTickets` (operasional: hanya tiket aktif)
// Kalau kriterianya ditulis ulang di sini, angkanya akan menyimpang dari Status Hutang/Piutang.
import { esc, rp, ageDays, fillTpl, pecahUnit, tglID } from '@domain/format';
import { currentUser } from '@core/session';
import { kebijakan, waTpl } from '@core/settings';
import { toast } from '@ui/toast';
import { piutangTiket } from '@domain/money';
import { bolehKirimLagi, sisaJedaHari } from '@domain/blast';
import { openDetail } from '@modules/queue/queue.view';
import { fetchPerluTickets } from '@modules/perlu/perlu.repo';
import { listPayable } from '@modules/payments/payments.repo';
import * as repo from './penagihan.repo';

type Tab = 'tagih' | 'ambil';
let pgTab: Tab = 'tagih';
let pgCabang = '';
let pgSembunyi = true;     // sembunyikan yang masih dalam masa jeda — sisanya = yang perlu dikerjakan

interface Baris {
  no: string; custNama: string; custWa: string | null; unit: string; outlet: string;
  nilai: number; jatuhTempo: string | null; umur: number; terakhir: number | null;
}

const KOLOM: Record<Tab, string> = { tagih: 'Sisa tagihan', ambil: 'Sudah menunggu' };

/**
 * `opts.tab` = tab yang ditampilkan (dikendalikan dari luar saat dipasang sebagai sub-tab
 * menu ⚠️ Perlu Diperhatikan). `opts.tanpaKepala` = jangan gambar judul & tombol tab sendiri —
 * dua baris tab bertumpuk membingungkan, yang di luar sudah menampilkannya.
 */
export async function renderPenagihan(root: HTMLElement, opts?: { tab?: 'tagih' | 'ambil'; tanpaKepala?: boolean }): Promise<void> {
  const u = currentUser(); if (!u) return;
  if (opts?.tab) pgTab = opts.tab;
  const kepala = !opts?.tanpaKepala;
  root.innerHTML = '<div class="panel">Memuat…</div>';
  const jeda = kebijakan().jedaIngatHari;

  let semua: Record<Tab, Baris[]>;
  try {
    const [payables, aktif, terakhir] = await Promise.all([
      listPayable(), fetchPerluTickets(), repo.lastReminders(jeda),
    ]);
    const last = (no: string): number | null => terakhir.get(no) ?? null;
    semua = {
      tagih: payables
        .filter((p) => piutangTiket(p.ticket) > 0)
        .map((p) => ({
          no: p.ticket.no, custNama: p.custNama, custWa: p.custWa, unit: p.unit,
          outlet: p.ticket.outlet ?? '—', nilai: piutangTiket(p.ticket),
          jatuhTempo: p.ticket.jatuhTempo ?? null, umur: ageDays(p.ticket.jatuhTempo ?? null), terakhir: last(p.ticket.no),
        })),
      ambil: aktif
        .filter((t) => t.status === 'Siap Diambil')
        .map((t) => ({
          no: t.no, custNama: t.custNama, custWa: t.custWa, unit: t.unit,
          outlet: t.outlet, nilai: 0, jatuhTempo: null, umur: ageDays(t.tglISO), terakhir: last(t.no),
        })),
    };
  } catch (e) { root.innerHTML = `<div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const kini = Date.now();
  // Paling mendesak di atas: piutang paling lama lewat tempo / unit paling lama menunggu.
  semua.tagih.sort((a, b) => b.umur - a.umur);
  semua.ambil.sort((a, b) => b.umur - a.umur);

  const cabangOpts = [...new Set([...semua.tagih, ...semua.ambil].map((b) => b.outlet).filter((c) => c && c !== '—'))].sort();
  const cocok = (b: Baris): boolean => (!pgCabang || b.outlet === pgCabang);
  const baris = semua[pgTab].filter(cocok);
  const belum = baris.filter((b) => bolehKirimLagi(b.terakhir, jeda, kini));
  const tampil = pgSembunyi ? belum : baris;
  const sudah = baris.length - belum.length;

  const tabBtn = (k: Tab, l: string, n: number): string =>
    `<button class="btn ${pgTab === k ? 'primary' : ''}" data-pgtab="${k}" style="padding:6px 12px;font-size:13px">${l} <b>${n}</b></button>`;

  const rows = tampil.map((b) => {
    const bisa = bolehKirimLagi(b.terakhir, jeda, kini);
    const kapan = b.terakhir
      ? `<span style="color:${bisa ? 'var(--muted)' : 'var(--ok)'}">${new Date(b.terakhir).toLocaleDateString('id-ID')}${bisa ? '' : ` · tunggu ${sisaJedaHari(b.terakhir, jeda, kini)} hr`}</span>`
      : '<span style="color:var(--muted)">—</span>';
    const nilai = pgTab === 'tagih'
      ? `<b>${rp(b.nilai)}</b>${b.jatuhTempo ? `<br><span style="font-size:11px;color:${b.umur > 0 ? 'var(--danger)' : 'var(--muted)'}">jatuh tempo ${esc(tglID(b.jatuhTempo))}${b.umur > 0 ? ` · lewat ${b.umur} hr` : ''}</span>` : ''}`
      : `${b.umur} hari`;
    return `<tr data-no="${esc(b.no)}">
      <td><a href="#" class="pg-detail" data-no="${esc(b.no)}">${esc(b.no)}</a></td>
      <td>${esc(b.custNama)}</td>
      <td>${esc(b.unit)}</td>
      <td>${nilai}</td>
      <td class="pg-kapan">${kapan}</td>
      <td>${b.custWa
        ? `<button class="btn pg-kirim" data-no="${esc(b.no)}" style="padding:3px 10px;font-size:12px">💬 Kirim</button>`
        : '<span style="font-size:11.5px;color:var(--danger)">No. WA kosong</span>'}</td></tr>`;
  }).join('');

  root.innerHTML = `
    ${kepala ? '<div class="h1">📣 Penagihan & Pengingat</div>' : ''}
    <div class="scope-note">Kirim satu per satu lewat WhatsApp — <b>bukan blast otomatis</b>: WhatsApp tidak mengizinkan aplikasi mengirim massal sendiri. Daftar ini mengingat siapa yang sudah dihubungi, jadi pekerjaan bisa dijeda dan dilanjut nanti tanpa menagih orang yang sama dua kali. Isi pesan diatur di Pengaturan → 📄 Dokumen &amp; Template; jeda hubungi-ulang di ⏱️ SLA &amp; Waktu.</div>
    <div class="toolbar" style="margin:10px 0">
      ${kepala ? tabBtn('tagih', '📒 Tagih Piutang', semua.tagih.filter(cocok).length) + tabBtn('ambil', '📦 Ingatkan Ambil Unit', semua.ambil.filter(cocok).length) : ''}
      ${cabangOpts.length > 1 ? `<select id="pg_cab"><option value="">Semua cabang</option>${cabangOpts.map((c) => `<option value="${esc(c)}" ${pgCabang === c ? 'selected' : ''}>${esc(c)}</option>`).join('')}</select>` : ''}
      <label style="display:flex;align-items:center;gap:5px;font-size:12.5px"><input type="checkbox" id="pg_sembunyi" ${pgSembunyi ? 'checked' : ''}> Sembunyikan yang baru dihubungi</label>
    </div>
    <div class="panel" style="padding:10px">
      <div style="font-size:13px;margin-bottom:8px">Perlu dihubungi: <b>${belum.length}</b>${sudah ? ` · sudah dihubungi ${jeda} hari terakhir: <b style="color:var(--ok)">${sudah}</b>` : ''}</div>
      ${tampil.length ? `<table class="data"><thead><tr>
        <th>No. Servis</th><th>Pelanggan</th><th>Unit</th><th>${KOLOM[pgTab]}</th><th>Terakhir dihubungi</th><th>Aksi</th>
      </tr></thead><tbody>${rows}</tbody></table>`
      : `<div class="empty">${belum.length === 0 && sudah > 0 ? '✅ Semua sudah dihubungi. Hilangkan centang di atas untuk melihat daftarnya.' : 'Tidak ada yang perlu dihubungi.'}</div>`}
    </div>`;

  const ulang = (): void => { void renderPenagihan(root, opts); };
  root.querySelectorAll<HTMLElement>('button[data-pgtab]').forEach((b) => b.addEventListener('click', () => { pgTab = b.dataset['pgtab'] as Tab; ulang(); }));
  root.querySelector('#pg_cab')?.addEventListener('change', (e) => { pgCabang = (e.target as HTMLSelectElement).value; ulang(); });
  root.querySelector('#pg_sembunyi')?.addEventListener('change', (e) => { pgSembunyi = (e.target as HTMLInputElement).checked; ulang(); });
  root.querySelectorAll<HTMLElement>('a.pg-detail').forEach((a) => a.addEventListener('click', (ev) => {
    ev.preventDefault(); void openDetail(a.dataset['no'] ?? '', ulang);
  }));

  root.querySelectorAll<HTMLButtonElement>('button.pg-kirim').forEach((b) => b.addEventListener('click', () => {
    const row = tampil.find((x) => x.no === (b.dataset['no'] ?? '')); if (!row) return;
    // Daftar boleh menampilkan yang masih dalam masa jeda (centang dilepas) — tapi mengirimnya
    // harus disengaja, jangan sampai pelanggan yang sama ditagih lagi karena salah klik.
    const sisaJ = sisaJedaHari(row.terakhir, jeda, kini);
    if (sisaJ > 0 && !confirm(`${row.custNama} baru dihubungi. Jadwalnya ${sisaJ} hari lagi. Tetap kirim sekarang?`)) return;
    b.disabled = true;                                   // klik ganda = dua jendela WA + dua catatan
    const un = pecahUnit(row.unit);
    const isi = pgTab === 'tagih'
      ? fillTpl(waTpl().tagih, {
        nama: row.custNama, no: row.no, merk: un.merk, tipe: un.tipe, unit: row.unit,
        sisa: rp(row.nilai), jt: row.jatuhTempo ? ` (jatuh tempo ${tglID(row.jatuhTempo)})` : '',
      })
      : fillTpl(waTpl().siapLaporan, { nama: row.custNama, no: row.no, merk: un.merk, tipe: un.tipe, unit: row.unit });
    repo.kirimPengingat(row.no, row.custWa, isi, pgTab === 'tagih' ? 'Tagih piutang' : 'Pengingat ambil unit', toast);
    b.outerHTML = '<span style="color:var(--ok);font-size:12px">✔ terkirim</span>';
    const sel = root.querySelector(`tr[data-no="${CSS.escape(row.no)}"] .pg-kapan`);
    if (sel) sel.innerHTML = `<span style="color:var(--ok)">${new Date().toLocaleDateString('id-ID')} · baru saja</span>`;
  }));
}
